import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, of, throwError, forkJoin } from 'rxjs';
import { map, catchError, tap, switchMap, shareReplay, take } from 'rxjs/operators';

import {
  UnifiedPropertyFinancials,
  UnifiedLoanFinancials,
  DataQualityResult} from '@shared/definitions/unified-financial-data.model';
import {
  createTemporalAmount
} from '@shared/definitions/unified-financial-data.model';

import { DataValidationService } from '@core/services/finance/data-validation.service';
import { DataNormalizationService } from '@core/services/finance/data-normalization.service';
import { 
  StandardizedKPICalculatorService, 
  PropertyKPIResults, 
  YearlyCashflowTable 
} from '@core/services/finance/standardized-kpi-calculator.service';

// Import services for raw data access
import { LoanStore } from '@shared/services/loan.store';
import { ContractStore } from '@shared/services/contract.store';
import { PropertyStore } from '@shared/services/property.store';

// Import data types
import { Loan } from '@shared/definitions/loan';
import { Contract } from '@shared/definitions/contract';

/**
 * UNIFIED FINANCIAL CALCULATION SERVICE
 * 
 * THE SINGLE SOURCE OF TRUTH for all financial calculations in the application.
 * This service replicates the excellence achieved in the Investment Simulator
 * by providing centralized, consistent, and standardized calculations.
 * 
 * Key responsibilities:
 * - Integrate all foundation services (normalization, validation, KPI calculation)
 * - Provide reactive streams for property financial data
 * - Cache calculations for performance
 * - Ensure data consistency across all components
 * - Follow international real estate standards
 * 
 * Architecture Pattern:
 * Raw Data → Normalization → Validation → KPI Calculation → Cached Results
 */
@Injectable({
  providedIn: 'root'
})
export class UnifiedFinancialCalculationService {

  // Cache for property calculations to avoid redundant processing
  private propertyKPICache = new Map<string, PropertyKPIResults>();
  private propertyCashflowCache = new Map<string, YearlyCashflowTable>();
  private propertyDataCache = new Map<string, UnifiedPropertyFinancials>();
  
  // Loading states
  private loadingStates = new Map<string, BehaviorSubject<boolean>>();
  private errorStates = new Map<string, BehaviorSubject<string | null>>();

  constructor(
    private dataValidation: DataValidationService,
    private dataNormalization: DataNormalizationService,
    private kpiCalculator: StandardizedKPICalculatorService,
    private loanStore: LoanStore,
    private contractStore: ContractStore,
    private propertyStore: PropertyStore
  ) {
  }

  /**
   * MAIN METHOD: Get comprehensive property financial analysis
   * This is the primary entry point that most components should use
   */
  getPropertyFinancialAnalysis(propertyId: string): Observable<PropertyFinancialAnalysis> {
    if (!propertyId) {
      return throwError(() => new Error('Property ID is required'));
    }

    console.log(`[UnifiedFinancialCalculationService] 🔍 Starting analysis for property ${propertyId}`);
    this.setLoadingState(propertyId, true);
    this.clearErrorState(propertyId);

    return this.loadAndNormalizePropertyData(propertyId).pipe(
      switchMap(normalizedData => this.performFinancialAnalysis(normalizedData)),
      tap(analysis => {
        console.log(`[UnifiedFinancialCalculationService] Analysis complete for ${propertyId}:`, {
          dataQuality: analysis.unifiedData.dataQuality,
          grossYield: analysis.kpis.grossYield.toFixed(2) + '%',
          capRate: analysis.kpis.capRate.toFixed(2) + '%',
          netCashFlow: '€' + analysis.kpis.netCashFlow.toLocaleString()
        });
        this.setLoadingState(propertyId, false);
      }),
      catchError(error => {
        console.error(`[UnifiedFinancialCalculationService] Error analyzing property ${propertyId}:`, error);
        this.setErrorState(propertyId, error.message);
        this.setLoadingState(propertyId, false);
        return throwError(() => error);
      }),
      shareReplay(1) // Cache the result
    );
  }

  /**
   * Get property KPIs only (lighter weight for dashboard usage)
   */
  getPropertyKPIs(propertyId: string): Observable<PropertyKPIResults> {
    return this.getPropertyFinancialAnalysis(propertyId).pipe(
      map(analysis => analysis.kpis)
    );
  }

  /**
   * Get property cashflow table only
   */
  getPropertyCashflowTable(propertyId: string, projectionYears = 10): Observable<YearlyCashflowTable> {
    return this.getPropertyFinancialAnalysis(propertyId).pipe(
      map(analysis => {
        // Check if we need to regenerate with different projection years
        if (analysis.cashflowTable.projectionYears !== projectionYears) {
          return this.kpiCalculator.generateYearlyCashflowTable(
            analysis.unifiedData, 
            analysis.loanData, 
            projectionYears
          );
        }
        return analysis.cashflowTable;
      })
    );
  }

  /**
   * Get unified property data only (for forms and detailed views)
   */
  getUnifiedPropertyData(propertyId: string): Observable<UnifiedPropertyFinancials> {
    return this.getPropertyFinancialAnalysis(propertyId).pipe(
      map(analysis => analysis.unifiedData)
    );
  }

  /**
   * Get loading state for a property
   */
  getLoadingState(propertyId: string): Observable<boolean> {
    if (!this.loadingStates.has(propertyId)) {
      this.loadingStates.set(propertyId, new BehaviorSubject<boolean>(false));
    }
    return this.loadingStates.get(propertyId)!.asObservable();
  }

  /**
   * Get error state for a property
   */
  getErrorState(propertyId: string): Observable<string | null> {
    if (!this.errorStates.has(propertyId)) {
      this.errorStates.set(propertyId, new BehaviorSubject<string | null>(null));
    }
    return this.errorStates.get(propertyId)!.asObservable();
  }

  /**
   * Invalidate cache for a property (call when data changes)
   */
  invalidatePropertyCache(propertyId: string): void {
    this.propertyKPICache.delete(propertyId);
    this.propertyCashflowCache.delete(propertyId);
    this.propertyDataCache.delete(propertyId);
  }

  /**
   * Invalidate all caches (call on major data updates)
   */
  invalidateAllCaches(): void {
    this.propertyKPICache.clear();
    this.propertyCashflowCache.clear();
    this.propertyDataCache.clear();
  }

  /**
   * Get cache statistics for monitoring
   */
  getCacheStats(): CacheStatistics {
    return {
      kpiCacheSize: this.propertyKPICache.size,
      cashflowCacheSize: this.propertyCashflowCache.size,
      dataCacheSize: this.propertyDataCache.size,
      totalCachedProperties: new Set([
        ...this.propertyKPICache.keys(),
        ...this.propertyCashflowCache.keys(),
        ...this.propertyDataCache.keys()
      ]).size
    };
  }

  // PRIVATE METHODS

  /**
   * Load and normalize property data using our standardized pipeline
   */
  private loadAndNormalizePropertyData(propertyId: string): Observable<UnifiedPropertyFinancials> {
    // Check cache first
    if (this.propertyDataCache.has(propertyId)) {
      return of(this.propertyDataCache.get(propertyId)!);
    }

    
    return new Observable<UnifiedPropertyFinancials>(observer => {
      this.dataNormalization.normalizePropertyFinancials(propertyId)
        .then(normalizedData => {
          // Validate the normalized data
          const validation = this.validateNormalizedData(normalizedData);
          if (!validation.isValid) {
            console.warn(`[UnifiedFinancialCalculationService] Data validation issues for ${propertyId}:`, validation.issues);
            // Continue with warnings, but log them
          }

          // Cache the result
          this.propertyDataCache.set(propertyId, normalizedData);
          
          observer.next(normalizedData);
          observer.complete();
        })
        .catch(error => {
          console.error(`[UnifiedFinancialCalculationService] Failed to normalize data for ${propertyId}:`, error);
          observer.error(error);
        });
    });
  }

  /**
   * Perform comprehensive financial analysis
   */
  private performFinancialAnalysis(propertyData: UnifiedPropertyFinancials): Observable<PropertyFinancialAnalysis> {
    console.log(`[UnifiedFinancialCalculationService] 🧮 Performing financial analysis for ${propertyData.propertyId}`);

    return new Observable<PropertyFinancialAnalysis>(observer => {
      // Load actual raw data from all services for real data aggregation
      console.log(`[UnifiedFinancialCalculationService] 🏦 Loading raw data for property ${propertyData.propertyId}`);

      // NB: loans are already loaded reactively by LoansService.initializeService()
      // (auth/workspace-gated effect → fetchAllLoans → LoanStore bridge). We must
      // NOT call loanStore.fetchAll() here: this runs once per property, per
      // enrichment pass, and re-sets the LoanStore signal with brand-new Loan
      // refs — which the dashboard's combineLatest([…, loansSig]) reads, so it
      // re-emits and re-triggers this very pipeline → infinite recompute loop
      // (flooding the "Property value is zero" KPI warnings). Read the already-
      // loaded snapshot synchronously instead; late-arriving loans trigger one
      // debounced reactive recompute via the bridge, not a loop.

      // Load all raw data sources in parallel
      const rawDataObservable = forkJoin({
        rawLoans: of(this.loanStore.getForProperty(propertyData.propertyId).slice()).pipe(
          tap(loans => console.log(`[UnifiedFinancialCalculationService] ✅ Loans loaded: ${loans.length} loans`)),
          tap({ complete: () => console.log(`[UnifiedFinancialCalculationService] 🏁 Loans observable completed`) }),
          catchError((error: unknown) => {
            console.error(`[UnifiedFinancialCalculationService] ❌ Failed to load loan data for ${propertyData.propertyId}:`, error);
            return of([] as Loan[]);
          }),
          take(1)
        ),
        rawContracts: of(this.contractStore.getForProperty(propertyData.propertyId).slice()).pipe(
          tap(contracts => console.log(`[UnifiedFinancialCalculationService] ✅ Contracts loaded: ${contracts.length} contracts`)),
          tap({ complete: () => console.log(`[UnifiedFinancialCalculationService] 🏁 Contracts observable completed`) }),
          catchError((error: unknown) => {
            console.error(`[UnifiedFinancialCalculationService] ❌ Failed to load contract data for ${propertyData.propertyId}:`, error);
            return of([] as Contract[]);
          }),
          take(1)
        ),
        rawProperty: of(this.propertyStore.getById(propertyData.propertyId)).pipe(
          tap(property => console.log(`[UnifiedFinancialCalculationService] ✅ Property loaded: ${property ? 'found' : 'not found'}`)),
          tap({ complete: () => console.log(`[UnifiedFinancialCalculationService] 🏁 Property observable completed`) }),
          catchError((error: unknown) => {
            console.error(`[UnifiedFinancialCalculationService] ❌ Failed to load property data for ${propertyData.propertyId}:`, error);
            return of(null);
          }),
          take(1)
        )
      });

      console.log(`[UnifiedFinancialCalculationService] 🎯 About to subscribe to rawDataObservable...`);

      rawDataObservable.subscribe({
        next: ({ rawLoans, rawContracts, rawProperty }) => {
          console.log(`[UnifiedFinancialCalculationService] 📊 Raw data loaded for real aggregation:`, {
            rawLoans: rawLoans.length,
            rawContracts: rawContracts.length,
            hasRawProperty: !!rawProperty,
            activeContracts: rawContracts.filter((c: Contract) => c.status === 'Active').length
          });
          
          // Normalize each loan to unified format
          const normalizedLoans: UnifiedLoanFinancials[] = rawLoans.map(loan => {
            try {
              return this.dataNormalization.normalizeLoanFinancials(loan);
            } catch (error) {
              console.error(`[UnifiedFinancialCalculationService] Error normalizing loan ${loan.loanUID}:`, error);
              // Return a minimal unified loan structure as fallback
              return {
                loanId: loan.loanUID || 'unknown',
                propertyId: propertyData.propertyId,
                originalAmount: createTemporalAmount(loan.capital?.nominalAmount || 0, 'annually', 'EUR', 'estimated', 0.5),
                currentBalance: createTemporalAmount(loan.capital?.nominalAmount || 0, 'annually', 'EUR', 'estimated', 0.5),
                interestRate: (loan.interest?.rate || 0) * 100,
                termYears: loan.numberOfMonths ? loan.numberOfMonths / 12 : 0,
                paymentFrequency: 'monthly' as const,
                regularPayment: createTemporalAmount(rawLoans.length > 0 && loan.loanTable?.[0]?.totalCashout || 0, 'monthly', 'EUR', 'estimated', 0.5),
                principalPayment: createTemporalAmount(loan.loanTable?.[0]?.principalRepayment || 0, 'monthly', 'EUR', 'estimated', 0.5),
                interestPayment: createTemporalAmount(loan.loanTable?.[0]?.interest || 0, 'monthly', 'EUR', 'estimated', 0.5),
                startDate: new Date(),
                maturityDate: new Date(),
                amortizationSchedule: [],
                originationFees: createTemporalAmount(0, 'annually', 'EUR', 'estimated', 0.5),
                loanType: 'fixed' as const,
                status: 'active' as const,
                dataQuality: 'low' as const,
                lastUpdated: new Date()
              };
            }
          });
          
          console.log(`[UnifiedFinancialCalculationService] Unified loan data processed: ${normalizedLoans.length} loans`, normalizedLoans.map(loan => ({
            loanId: loan.loanId,
            originalAmount: loan.originalAmount.amount,
            regularPayment: `€${loan.regularPayment.amount}/${loan.regularPayment.period}`,
            annualDebtService: this.kpiCalculator.convertToAnnualAmount(loan.regularPayment)
          })));

          console.log(`[UnifiedFinancialCalculationService] 📊 Starting KPI calculations with data:`, {
            propertyId: propertyData.propertyId,
            baseRent: `€${propertyData.baseRent.amount}/month`,
            currentValue: `€${propertyData.currentValue.amount.toLocaleString()}`,
            propertyTax: `€${propertyData.propertyTax.amount.toLocaleString()}`,
            maintenance: `€${propertyData.maintenance.amount.toLocaleString()}`,
            loanCount: normalizedLoans.length
          });

          // Calculate KPIs using standardized formulas
          const kpis = this.kpiCalculator.calculatePropertyKPIs(propertyData, normalizedLoans);
          
          // Generate cashflow table - NOW WITH FULL REAL DATA AGGREGATION
          // Passes raw loans, contracts, and property for maximum data accuracy
          const cashflowTable = this.kpiCalculator.generateYearlyCashflowTable(
            propertyData, 
            normalizedLoans, 
            10, // projection years
            rawLoans, // Raw loan data for LoanAdapterService.calculateDebtService()
            rawContracts, // Raw contract data for planned payments
            rawProperty || undefined // Raw property data for acquisition dates
          );

          // Cache results
          this.propertyKPICache.set(propertyData.propertyId, kpis);
          this.propertyCashflowCache.set(propertyData.propertyId, cashflowTable);

          const analysis: PropertyFinancialAnalysis = {
            propertyId: propertyData.propertyId,
            unifiedData: propertyData,
            loanData: normalizedLoans,
            kpis,
            cashflowTable,
            dataQuality: this.assessOverallDataQuality(propertyData, normalizedLoans),
            lastCalculated: new Date(),
            calculatedBy: 'UnifiedFinancialCalculationService - Real Data Aggregation'
          };

          console.log(`[UnifiedFinancialCalculationService] ✅ Financial analysis completed using REAL DATA AGGREGATION for ${propertyData.propertyId}:`, {
            capRate: `${kpis.capRate.toFixed(2)}%`,
            expenseRatio: `${kpis.expenseRatio.toFixed(2)}%`,
            netOperatingIncome: `€${kpis.netOperatingIncome.toLocaleString()}`,
            totalDebtService: `€${normalizedLoans.reduce((sum: number, loan: UnifiedLoanFinancials) => sum + this.kpiCalculator.convertToAnnualAmount(loan.regularPayment), 0).toLocaleString()}`,
            grossYield: `${kpis.grossYield.toFixed(2)}%`,
            dataQuality: analysis.dataQuality,
            dataAggregationApproach: 'Real data from LoanAdapterService and contract payments'
          });

          observer.next(analysis);
          observer.complete();
        },
        error: (error: unknown) => {
          console.error(`[UnifiedFinancialCalculationService] 💥 Subscribe error in performFinancialAnalysis:`, error);
          observer.error(error);
        },
        complete: () => {
          console.log(`[UnifiedFinancialCalculationService] 🏁 ForkJoin rawDataObservable completed successfully`);
        }
      });
    });
  }

  /**
   * Validate normalized data quality
   */
  private validateNormalizedData(data: UnifiedPropertyFinancials): DataValidationResult {
    const issues: string[] = [];
    const warnings: string[] = [];

    // Validate base rent
    const rentValidation = this.dataValidation.validateTemporalAmount(data.baseRent);
    if (!rentValidation.isValid) {
      issues.push(...rentValidation.errors);
    }
    warnings.push(...rentValidation.warnings);

    // Validate property value
    const valueValidation = this.dataValidation.validateTemporalAmount(data.currentValue);
    if (!valueValidation.isValid) {
      issues.push(...valueValidation.errors);
    }
    warnings.push(...valueValidation.warnings);

    // Check for reasonable data ranges
    if (data.baseRent.amount > 0 && data.currentValue.amount > 0) {
      const grossYield = (data.baseRent.amount * 12 / data.currentValue.amount) * 100;
      if (grossYield > 20) {
        warnings.push(`Gross yield ${grossYield.toFixed(1)}% seems unusually high`);
      }
      if (grossYield < 1) {
        warnings.push(`Gross yield ${grossYield.toFixed(1)}% seems unusually low`);
      }
    }

    return {
      isValid: issues.length === 0,
      issues,
      warnings
    };
  }

  /**
   * Assess overall data quality
   */
  private assessOverallDataQuality(
    propertyData: UnifiedPropertyFinancials, 
    _loanData: UnifiedLoanFinancials[]
  ): DataQualityResult {
    return this.dataValidation.calculateDataQuality({
      baseRent: propertyData.baseRent,
      currentValue: propertyData.currentValue,
      lastUpdated: propertyData.lastUpdated,
      propertyTax: propertyData.propertyTax,
      insurance: propertyData.insurance,
      rentGrowthRate: propertyData.rentGrowthRate
    });
  }

  /**
   * Set loading state for a property
   */
  private setLoadingState(propertyId: string, loading: boolean): void {
    if (!this.loadingStates.has(propertyId)) {
      this.loadingStates.set(propertyId, new BehaviorSubject<boolean>(loading));
    } else {
      this.loadingStates.get(propertyId)!.next(loading);
    }
  }

  /**
   * Set error state for a property
   */
  private setErrorState(propertyId: string, error: string): void {
    if (!this.errorStates.has(propertyId)) {
      this.errorStates.set(propertyId, new BehaviorSubject<string | null>(error));
    } else {
      this.errorStates.get(propertyId)!.next(error);
    }
  }

  /**
   * Clear error state for a property
   */
  private clearErrorState(propertyId: string): void {
    if (this.errorStates.has(propertyId)) {
      this.errorStates.get(propertyId)!.next(null);
    }
  }
}

// Result interfaces
export interface PropertyFinancialAnalysis {
  propertyId: string;
  unifiedData: UnifiedPropertyFinancials;
  loanData: UnifiedLoanFinancials[];
  kpis: PropertyKPIResults;
  cashflowTable: YearlyCashflowTable;
  dataQuality: DataQualityResult;
  lastCalculated: Date;
  calculatedBy: string;
}

export interface DataValidationResult {
  isValid: boolean;
  issues: string[];
  warnings: string[];
}

export interface CacheStatistics {
  kpiCacheSize: number;
  cashflowCacheSize: number;
  dataCacheSize: number;
  totalCachedProperties: number;
} 